// src/components/PayControls.tsx — PAY-01 in the Dispatch job panel: pending counter-offers (owner accepts & assigns,
// or rejects) and the job's payout (status, when it pays; owner holds / releases). Every number comes from the
// database (ops_job_detail); every change goes through obra-accept-job v13 or an owner-only ops_ function, with a reason.
import { useState } from "react";
import { ops, type CounterOffer, type JobDetail, type JobPayout } from "../lib/ops";
import { dateTime, marginTone, money, pct } from "../lib/format";
import { useOperator, useServices, useToast } from "../lib/context";
import { Notice, SectionLabel } from "./ui";

// SP-POST-09a: owner counter rules — 25% margin auto, 25–18% owner approval, never below 18%.
const BAND_LABEL: Record<string, string> = {
  auto: "Within 25% band — the system approves this on its own",
  approval: "Needs your approval (25%–18% margin)",
  above_floor: "Past the 18% floor — can't be accepted",
  no_caps: "No caps on this job — your call",
};

const REVIEW_LABEL: Record<string, string> = {
  legacy_rate: "the service has no hours on its rate card, only a legacy rate",
  estimated_44: "the service has no hours on its rate card; the margin is estimated",
  unmapped_service: "this kind of job isn't mapped to a catalog service",
  no_price: "the job has no price",
  unknown_tier: "the Maestro has no pay level",
  no_tier_payout: "the rate card has no payout for this Maestro's pay level",
  no_contractor: "no Maestro was assigned",
};

function MarginText({ cents, pctValue }: { cents: number | null; pctValue: number | null }) {
  const services = useServices();
  const target = services.data?.settings.target_margin_pct ?? 35;
  const floor = services.data?.settings.margin_floor_pct ?? 25;
  if (cents === null) return <>—</>;
  const tone = marginTone(pctValue, cents < 0, target, floor);
  return (
    <span className={`margin ${tone ?? ""}`}>
      {cents < 0 ? `loses ${money(-cents)}` : `leaves ${money(cents)}`}
      {pctValue !== null ? ` (${pct(pctValue)})` : ""}
    </span>
  );
}

function ReasonForm(props: {
  title: string;
  what: string;
  texts?: string[];
  confirmLabel: string;
  danger?: boolean;
  ariaLabel: string;
  onConfirm: (reason: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = reason.trim().length >= 5;
  return (
    <div className="inline-form">
      <p className="action-title">{props.title}</p>
      <p>{props.what}</p>
      {props.texts && props.texts.length > 0 && (
        <ul className="plain-list">
          {props.texts.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      )}
      <textarea
        className="reason"
        rows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        aria-label={props.ariaLabel}
        placeholder="Why? This goes in the change log."
      />
      {!ok && reason.length > 0 && <p className="hint">At least 5 characters.</p>}
      {error && <Notice kind="error">{error}</Notice>}
      <div className="row-actions">
        <button
          type="button"
          className={props.danger ? "btn danger" : "btn primary"}
          disabled={!ok || busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await props.onConfirm(reason.trim());
            } catch (e) {
              setError((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Saving…" : props.confirmLabel}
        </button>
        <button type="button" className="btn quiet" onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** Pending counter-offers ("Mi Precio"). Accepting assigns the job to that Maestro at their price (owner only). */
export function CounterOffers(props: { detail: JobDetail; takenText: string; onChanged: () => void | Promise<void> }) {
  const { role } = useOperator();
  const toast = useToast();
  const counters = props.detail.counters ?? [];
  const [open, setOpen] = useState<{ c: CounterOffer; mode: "accept" | "reject" } | null>(null);
  if (counters.length === 0) return null;
  const isOwner = role === "owner";
  const job = props.detail.job;
  const others = (c: CounterOffer) =>
    counters.filter((x) => x.contractor_id !== c.contractor_id).length + props.detail.open_offers.length;

  return (
    <>
      <SectionLabel>Counter-offers ({counters.length})</SectionLabel>
      {(job.auto_cap_cents || job.ceiling_cents) && (
        <p className="hint">
          {job.auto_cap_cents ? <>Approved automatically up to <span className="money">{money(job.auto_cap_cents)}</span> (25% margin). </> : null}
          {job.ceiling_cents ? <>Never above <span className="money">{money(job.ceiling_cents)}</span> (18% floor).</> : null}
        </p>
      )}
      <ul className="plain-list counter-list">
        {counters.map((c) => (
          <li key={c.counter_id} className="counter-row">
            <span>
              <strong>{c.maestro ?? "A Maestro"}</strong>
              {c.titulo ? ` (${c.titulo})` : ""} offers <span className="money">{money(c.offered_rate_cents)}</span>
              {c.price_cents !== null ? <> · price {money(c.price_cents)} · </> : " · "}
              <MarginText cents={c.margin_cents} pctValue={c.margin_pct} />
              {c.band && <span className={`hint band-${c.band}`}> · {BAND_LABEL[c.band]}</span>}
            </span>
            {isOwner && !open && (
              <span className="row-actions">
                <button type="button" className="btn primary" disabled={c.band === "above_floor"}
                  title={c.band === "above_floor" ? "Past the 18% margin floor — reject it or ask for a lower price" : undefined}
                  onClick={() => setOpen({ c, mode: "accept" })}>
                  Accept &amp; assign…
                </button>
                <button type="button" className="btn" onClick={() => setOpen({ c, mode: "reject" })}>
                  Reject…
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {!isOwner && <p className="hint">Only the owner can accept or reject a counter-offer.</p>}
      {open && open.mode === "accept" && (
        <ReasonForm
          title={`Accept ${open.c.maestro ?? "this Maestro"}'s price and assign the job`}
          what={`Assigns ${open.c.maestro ?? "this Maestro"} to this job at ${money(open.c.offered_rate_cents)}. When the job is completed, ${
            open.c.maestro ?? "they"
          } is paid ${money(open.c.offered_rate_cents)} (24 hours later). Every other open offer and counter-offer closes.`}
          texts={others(open.c) > 0 ? [`${others(open.c)} other Maestro${others(open.c) === 1 ? "" : "s"} get this text (Spanish): ${props.takenText}`] : []}
          confirmLabel={`Accept & assign ${open.c.maestro ?? "this Maestro"}`}
          ariaLabel="Reason for accepting the counter-offer"
          onConfirm={async (reason) => {
            await ops.acceptCounter(props.detail.job.id, open.c.contractor_id, open.c.counter_id, reason);
            toast(`✓ ${open.c.maestro ?? "Maestro"} assigned at ${money(open.c.offered_rate_cents)}`);
            setOpen(null);
            await props.onChanged();
          }}
          onCancel={() => setOpen(null)}
        />
      )}
      {open && open.mode === "reject" && (
        <ReasonForm
          title={`Reject ${open.c.maestro ?? "this Maestro"}'s price`}
          what={`Declines the counter-offer of ${money(open.c.offered_rate_cents)}. The job stays open for other Maestros.`}
          confirmLabel="Reject counter-offer"
          danger
          ariaLabel="Reason for rejecting the counter-offer"
          onConfirm={async (reason) => {
            await ops.rejectCounter(open.c.counter_id, reason);
            toast("✓ Counter-offer rejected");
            setOpen(null);
            await props.onChanged();
          }}
          onCancel={() => setOpen(null)}
        />
      )}
    </>
  );
}

function payoutLine(p: JobPayout, maestro: string): string {
  switch (p.status) {
    case "scheduled":
      return `Pays ${money(p.payout_cents)} to ${maestro}${p.eligible_at ? ` on ${dateTime(p.eligible_at)}` : ""}.`;
    case "needs_review":
      return `Needs review: suggested ${money(p.suggested_cents)} for ${maestro}. Not paid automatically because ${
        REVIEW_LABEL[p.review_reason ?? ""] ?? "its amount isn't from the rate card"
      }.`;
    case "held":
      return `Held. ${maestro} isn't paid until the payout is released.`;
    case "processing":
      return `Being paid now: ${money(p.payout_cents)} to ${maestro}.`;
    case "paid":
      return `Paid ${money(p.payout_cents)} to ${maestro}${p.paid_at ? ` on ${dateTime(p.paid_at)}` : ""}.`;
    case "failed":
      return `The Stripe transfer to ${maestro} failed. It won't retry on its own.`;
    default:
      return "No payout recorded for this job.";
  }
}

/** The payout recorded at completion; the owner can hold it inside its 24-hour window, and release it. */
export function PayoutSection(props: { detail: JobDetail; onChanged: () => void | Promise<void> }) {
  const { role } = useOperator();
  const toast = useToast();
  const p = props.detail.payout;
  const [mode, setMode] = useState<"hold" | "release" | null>(null);
  if (!p || (!p.computed_at && p.status !== "paid")) return null;
  const maestro = props.detail.maestro?.name ?? "the Maestro";
  const isOwner = role === "owner";
  const canHold = p.status === "scheduled" || p.status === "needs_review";
  const canRelease = p.status === "held";

  return (
    <>
      <SectionLabel>Payout</SectionLabel>
      <p className={`payout-line payout-${p.status}`}>{payoutLine(p, maestro)}</p>
      <dl className="facts">
        <dt>Basis</dt>
        <dd>{p.basis === "counter" ? "Accepted counter-offer" : p.basis === "hours" ? "Rate card (hours)" : p.basis ?? "—"}</dd>
        <dt>Pay level</dt>
        <dd>{p.tier ?? "—"}</dd>
        <dt>Margin</dt>
        <dd>
          <MarginText cents={p.margin_cents} pctValue={p.margin_pct} />
        </dd>
      </dl>
      {p.status === "needs_review" && (
        <p className="hint">Paying a reviewed amount comes with the Money screen. To pay services like this automatically, give them hours in Pricing.</p>
      )}
      {isOwner && !mode && (canHold || canRelease) && (
        <div className="row-actions">
          {canHold && (
            <button type="button" className="btn" onClick={() => setMode("hold")}>
              Hold payout…
            </button>
          )}
          {canRelease && (
            <button type="button" className="btn" onClick={() => setMode("release")}>
              Release payout…
            </button>
          )}
        </div>
      )}
      {mode && (
        <ReasonForm
          title={mode === "hold" ? "Hold this payout" : "Release this payout"}
          what={
            mode === "hold"
              ? `Stops the payout to ${maestro}. Nothing is sent until you release it.`
              : `Puts the payout back where it was. If it was scheduled and its 24 hours have passed, it's paid at the next hourly run (:45).`
          }
          confirmLabel={mode === "hold" ? "Hold payout" : "Release payout"}
          danger={mode === "hold"}
          ariaLabel={mode === "hold" ? "Reason for holding the payout" : "Reason for releasing the payout"}
          onConfirm={async (reason) => {
            await ops.setPayoutHold(props.detail.job.id, mode === "hold", reason);
            toast(mode === "hold" ? "✓ Payout held" : "✓ Payout released");
            setMode(null);
            await props.onChanged();
          }}
          onCancel={() => setMode(null)}
        />
      )}
      {!isOwner && (canHold || canRelease) && <p className="hint">Only the owner can hold or release a payout.</p>}
    </>
  );
}
